import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Monitor, Plus, AlertTriangle, FileText } from "lucide-react";

/**
 * CreateITTicketModal — Diálogo de creación de un ticket interno IT.
 *
 * Extraído 1:1 desde `pages/ITTicketsPage.js`. Permite elegir tipo (Incidencia/Solicitud),
 * prioridad, título, descripción y asignación inicial de personal de soporte.
 *
 * @param {boolean}  props.createOpen          Apertura del diálogo.
 * @param {Function} props.setCreateOpen        Setter del flag de apertura.
 * @param {Object}   props.newTicket            Estado del formulario del nuevo ticket.
 * @param {Function} props.setNewTicket         Setter del formulario.
 * @param {Object}   props.priorityLabels       Mapa prioridad→etiqueta (para el desplegable).
 * @param {Array}    props.supportUsers         Usuarios asignables (personal de soporte).
 * @param {Function} props.handleCreateTicket   Handler de creación del ticket.
 */
export const CreateITTicketModal = ({
  createOpen,
  setCreateOpen,
  newTicket,
  setNewTicket,
  priorityLabels,
  supportUsers,
  handleCreateTicket,
}) => {
  return (
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Monitor className="h-5 w-5 text-[#03BEE4]" />
              Nuevo Ticket IT
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Tipo de Ticket *</Label>
                <Select
                  value={newTicket.ticket_type}
                  onValueChange={(v) => setNewTicket({ ...newTicket, ticket_type: v })}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    <SelectItem value="INCIDENCIA" className="text-white">
                      <span className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-400" />
                        Incidencia
                      </span>
                    </SelectItem>
                    <SelectItem value="SOLICITUD" className="text-white">
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-400" />
                        Solicitud
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Prioridad *</Label>
                <Select
                  value={newTicket.priority}
                  onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    {Object.entries(priorityLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Título *</Label>
              <Input
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                placeholder="Describe brevemente el problema o solicitud"
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
              />
            </div>

            <div>
              <Label className="text-slate-300">Descripción *</Label>
              <Textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                placeholder="Describe en detalle el problema o solicitud..."
                rows={4}
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
              />
            </div>

            {/* Assign at creation time. Same user source as the detail
                assignment selector (SOPORTE, fallback all active). */}
            <div data-testid="create-it-ticket-assign-block">
              <Label className="text-slate-300">Asignar a</Label>
              <Select
                value={newTicket.assigned_to_id || "none"}
                onValueChange={(v) => setNewTicket({
                  ...newTicket,
                  assigned_to_id: v === "none" ? "" : v,
                })}
              >
                <SelectTrigger
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  data-testid="create-it-ticket-assign-trigger"
                >
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                  <SelectItem value="none" className="text-slate-400">Sin asignar</SelectItem>
                  {supportUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-white">
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setCreateOpen(false)}
                className="border-[#1E1E5F] text-slate-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateTicket}
                className="bg-[#42E695] text-[#04042C]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Ticket
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
  );
};
